import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, type AppEvent } from "../api";
import { useAuth } from "../auth/AuthContext";
import {
  AccountCard,
  sortEventsNewestFirst,
  statusMeta,
} from "../components/AccountFeed";
import { ConfirmModal, ContextMenu } from "../components/ConfirmModal";
import { EventDetailModal, summaryFromEvent } from "../components/EventDetailModal";
import { useToast } from "../components/Toast";
import { isElectronDesktop } from "../desktop";
import { useLocalEventsQuery } from "../hooks/localEventsStore";

const HIDDEN_OPTS = {
  onlyHidden: true,
  includeHidden: true,
  limit: 2000,
} as const;

export function HiddenPage() {
  const { user } = useAuth();
  const { push } = useToast();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");
  const [httpHidden, setHttpHidden] = useState<AppEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState<{ x: number; y: number; ev: AppEvent } | null>(
    null
  );
  const [pendingUnhide, setPendingUnhide] = useState<AppEvent | null>(null);
  const [busy, setBusy] = useState(false);
  const desktop = isElectronDesktop();
  const isAdmin = user?.role === "admin";

  const { events: localHidden, ready } = useLocalEventsQuery(
    HIDDEN_OPTS,
    Boolean(desktop && isAdmin)
  );

  useEffect(() => {
    if (!isAdmin || desktop) {
      if (desktop) setLoading(!ready && localHidden.length === 0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void api<{ events: AppEvent[] }>("/api/admin/events/hidden")
      .then((data) => {
        if (!cancelled) {
          setHttpHidden(data.events || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          push(err instanceof Error ? err.message : "Failed to load hidden", "error");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, desktop, ready, localHidden.length, push]);

  const hidden = desktop
    ? localHidden.filter((e) => e.hidden)
    : httpHidden;

  const items = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return [...hidden]
      .filter((e) => {
        if (!q) return true;
        const s = summaryFromEvent(e);
        return (
          s.email.toLowerCase().includes(q) ||
          s.id.toLowerCase().includes(q) ||
          e.resourceKey.toLowerCase().includes(q) ||
          e.status.toLowerCase().includes(q)
        );
      })
      .sort(sortEventsNewestFirst);
  }, [hidden, filter]);

  const selectedLive = selected
    ? items.find((e) => e.id === selected.id) || selected
    : null;

  async function confirmUnhide() {
    if (!pendingUnhide) return;
    setBusy(true);
    try {
      const data = await api<{ event?: AppEvent }>(
        `/api/admin/events/${pendingUnhide.id}/unhide`,
        { method: "POST" }
      );
      if (data.event && window.brexDesktop?.applyServerEvent) {
        void window.brexDesktop.applyServerEvent(data.event);
      }
      push("Restored from Hidden", "ok");
      setPendingUnhide(null);
      setSelected(null);
    } catch (err) {
      push(err instanceof Error ? err.message : "Unhide failed", "error");
    } finally {
      setBusy(false);
    }
  }

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const meta = statusMeta("open");

  return (
    <div className="page page-wide page-fill">
      <header className="page-header">
        <h1>Hidden</h1>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter hidden…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            Bin
          </span>
        </div>
      </header>

      <p className="muted tiny page-blurb">
        Items sent here stay out of Live feed, Logins, and Assigned. Right-click to restore.
      </p>

      {loading && <p className="muted">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="muted empty-inline">Nothing hidden.</p>
      )}

      <div className="feed-column-body hidden-grid">
        {items.map((ev) => (
          <div
            key={ev.id}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu({ x: e.clientX, y: e.clientY, ev });
            }}
          >
            <AccountCard ev={ev} isAdmin onOpen={() => setSelected(ev)} />
          </div>
        ))}
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={[{ id: "unhide", label: "Restore" }]}
          onClose={() => setMenu(null)}
          onPick={(id) => {
            const ev = menu.ev;
            setMenu(null);
            if (id === "unhide") setPendingUnhide(ev);
          }}
        />
      )}

      {pendingUnhide && (
        <ConfirmModal
          title="Restore item?"
          body={
            <p>
              Put{" "}
              <strong>{summaryFromEvent(pendingUnhide).email}</strong> back into the live
              feeds?
            </p>
          }
          confirmLabel="Restore"
          busy={busy}
          onCancel={() => !busy && setPendingUnhide(null)}
          onConfirm={() => void confirmUnhide()}
        />
      )}

      {selectedLive && (
        <EventDetailModal event={selectedLive} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
